import { AxiosError } from 'axios';
import { useFormikContext } from 'formik';
import { Space } from 'antd';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import Button from 'components/new-ui/base/button/Button';
import SuitCaseIcon from 'components/icons/SuitCaseIcon';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import { CreateJobFormFields } from 'views/jobs/validationSchemas';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import Notification from 'components/ui/base/notification/Notification';
import { assignOwnerToJob, createJob } from 'store/jobs/client';
import useSession from 'hooks/useSession';
import { checkIsFreightForwarder } from 'config/roles/roles';
import useIndividuals from 'hooks/useIndividuals';
import { Individual } from 'store/individuals/individual';

type LocationState = {
    from?: string;
};

const CreateJobPageHeader = () => {
    const { userInfo } = useSession();
    const { getIndividual } = useIndividuals();
    const { isReadyToSubmit, isSubmitting } = useMultiStepFormContext<CreateJobFormFields>();
    const { values } = useFormikContext<CreateJobFormFields>();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    const teamIdParam = searchParams.get('teamId');
    const isFreightForwarderRole = checkIsFreightForwarder(userInfo);
    const state = location.state as LocationState | null;

    const handleSubmit = async () => {
        if (!values.customer.customerId || !values.job.jobId) {
            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'Job Creation Erorr',
                description: 'Could not create a job as the customer data is missing',
            });
            return;
        }

        try {
            showGlobalOverlay({
                type: 'LoadingOverlay',
                payload: {
                    message: 'Creating a job...',
                },
            });

            // We need to provide team id for Freight Forwarder roles
            let teamId = values.team.teamId;

            // Since we hide team section when we're creating the job from team, we extract the id from the search params
            if (teamIdParam) {
                teamId = teamIdParam;
            } else if (isFreightForwarderRole && !values.team.teamId) {
                if (!userInfo?.individualId) return;

                const individual: Individual = await getIndividual(userInfo.individualId);
                const activeTeam = individual.teamMemberships[0];
                teamId = activeTeam.teamId;
            }

            // 1. Create the job
            const createdJob = await createJob({
                customerId: values.customer.customerId,
                referenceId: values.job.jobId.trim(),
                incotermCode: values.deliveryTerms.incotermCode,
                teamId,
            });

            if (!createdJob.id) {
                throw new Error('Could not create a job. Please try again.');
            }
            Notification({
                placement: 'top',
                type: 'success',
                messageTitle: 'Job Created',
                description: 'Successfully created job',
            });
            // 2. If we have an assignedAgent in formik, assign the job to that agent
            if (values.assignAgent.assignedAgent) {
                showGlobalOverlay({
                    type: 'LoadingOverlay',
                    payload: {
                        message: 'Assigning Job...',
                    },
                });
                const updatedJob = await assignOwnerToJob(createdJob.id, values.assignAgent.assignedAgent);
                Notification({
                    placement: 'top',
                    type: 'success',
                    messageTitle: 'Job Assigned',
                    description: 'Successfully assigned job',
                });
                if (!updatedJob) {
                    throw new Error('Could not assign job to clearance agent');
                }
            }

            // Get the target location on success
            let targetLocation = `/jobs/${createdJob.id}`;

            if (teamIdParam) {
                targetLocation = `/teams/${teamIdParam}/jobs`;
            }

            if (state?.from) {
                navigate(state.from, { state: { job: createdJob } });
                return;
            }

            navigate(targetLocation, { replace: true });
        } catch (error) {
            let errorInfo = 'Something went wrong';
            if (error instanceof AxiosError && error.response?.data?.message) {
                errorInfo = error.response.data.message;
            }

            Notification({
                placement: 'top',
                type: 'error',
                messageTitle: 'Error',
                description: errorInfo,
            });
        } finally {
            hideGlobalOverlay();
        }
    };

    return (
        <PageHeader
            title="Create Job"
            icon={<SuitCaseIcon />}
            sidebar={
                <Space size={16}>
                    <Button onClick={() => navigate(-1)}>Cancel</Button>
                    <Button loading={isSubmitting} disabled={!isReadyToSubmit()} onClick={handleSubmit} type="primary">
                        Create Job
                    </Button>
                </Space>
            }
        />
    );
};

export default CreateJobPageHeader;
